import type { User } from '../../contexts/UserContext';
import { isPlatformAdministrator } from './appNavAccess';

/** Membership roles that may open company administration for the selected company. */
export const COMPANY_MANAGER_ROLES = ['admin', 'owner', 'company_admin', 'company-admin'] as const;

export function normalizeCompanyRole(role: string | null | undefined): string {
  return String(role || '').trim().toLowerCase();
}

export function isCompanyManagerRole(role: string | null | undefined): boolean {
  return (COMPANY_MANAGER_ROLES as readonly string[]).includes(normalizeCompanyRole(role));
}

/**
 * Company administration is allowed for a platform administrator, or for a
 * selected-company membership whose role is a company manager.
 * A platform administrator still needs a selected company before a page loads data.
 */
export function canManageSelectedCompany(
  user: User | null | undefined,
  membershipRole: string | null | undefined,
): boolean {
  if (!user) return false;
  if (isPlatformAdministrator(user)) return true;
  return isCompanyManagerRole(membershipRole);
}

/** Seat assignment uses company_subscriptions.id. subscription_id is a different row. */
export function companySubscriptionSeatId(row: {
  id?: string | null;
  subscription_id?: string | null;
}): string {
  const rowId = String(row.id || '').trim();
  if (!rowId) {
    throw new Error(
      'Company subscription row has no id. Seat assignment uses company_subscriptions.id.',
    );
  }
  return rowId;
}

export interface CompanyBookingRow {
  id: string;
  company_id?: string;
  company_name?: string;
}

/** Keep bookings that belong to the selected company. Rows with neither id nor name stay out. */
export function bookingsForCompany<T extends CompanyBookingRow>(
  rows: T[],
  companyId: string,
  companyName: string,
): { matched: T[]; omitted: number } {
  const id = companyId.trim();
  const name = companyName.trim().toLowerCase();
  const matched = rows.filter((row) => {
    const rowId = String(row.company_id || '').trim();
    if (rowId) return rowId === id;
    const rowName = String(row.company_name || '').trim().toLowerCase();
    return Boolean(name) && rowName === name;
  });
  return { matched, omitted: rows.length - matched.length };
}
